import type { MeetingPublic } from '@repo/types/meetings';

import { EVENT_COLOR } from '@repo/types/enums/eventColor';
import { VIEW_KEY } from '@repo/types/enums/viewKey';
import { isSameDay, isValid, parse } from 'date-fns';

/** Minutes step for calendar/meeting time pickers and rounding (e.g. 5 → 00, 05, 10, …). */
export const DEFAULT_TIME_STEP = 5;

/**
 * Get CSS classes for event colors
 */
export function getEventColorClasses(color?: EVENT_COLOR | string): string {
  const eventColor = color || EVENT_COLOR.MUTED;

  switch (eventColor) {
    case EVENT_COLOR.RED:
      return 'bg-fill-brand/50 hover:bg-fill-brand/40 text-text-on-brand-fill shadow-fill-brand/8';
    case EVENT_COLOR.YELLOW:
      return 'bg-fill-accent/50 hover:bg-fill-accent/40 text-text-on-accent-fill shadow-fill-accent/8';
    case EVENT_COLOR.PURPLE:
      return 'bg-fill-purple/50 hover:bg-fill-purple/40 text-text-primary shadow-fill-purple/8';
    case EVENT_COLOR.BLUE:
      return 'bg-fill-blue/50 hover:bg-fill-blue/40 text-text-primary shadow-fill-blue/8';
    case EVENT_COLOR.TURQUOISE:
      return 'bg-fill-turquoise/50 hover:bg-fill-turquoise/40 text-text-primary shadow-fill-turquoise/8';
    case EVENT_COLOR.MUTED:
      return 'bg-fill-secondary/75 hover:bg-fill-secondary/55 text-text-secondary shadow-fill-secondary/8';
    default:
      return 'bg-fill-brand/50 hover:bg-fill-brand/40 text-text-on-brand-fill shadow-fill-brand/8';
  }
}

/**
 * Get CSS classes for border radius based on event position in multi-day events
 */
export function getBorderRadiusClasses(isFirstDay: boolean, isLastDay: boolean): string {
  if (isFirstDay && isLastDay) {
    return 'rounded'; // Both ends rounded
  } else if (isFirstDay) {
    return 'rounded-l rounded-r-none'; // Only left end rounded
  } else if (isLastDay) {
    return 'rounded-r rounded-l-none'; // Only right end rounded
  } else {
    return 'rounded-none'; // No rounded corners
  }
}

/**
 * Check if an event is a multi-day event
 */
export function isMultiDayEvent<T extends MeetingPublic>(event: T): boolean {
  const eventStart = new Date(event.startDate);
  const eventEnd = new Date(event.endDate);
  return event.allDay || eventStart.getDate() !== eventEnd.getDate();
}

/**
 * Filter events for a specific day
 */
export function getEventsForDay<T extends MeetingPublic>(events: T[], day: Date): T[] {
  return events
    .filter((event) => {
      const eventStart = new Date(event.startDate);
      return isSameDay(day, eventStart);
    })
    .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
}

/**
 * Sort events with multi-day events first, then by start time
 */
export function sortEvents<T extends MeetingPublic>(events: T[]): T[] {
  return [...events].sort((a, b) => {
    const aIsMultiDay = isMultiDayEvent(a);
    const bIsMultiDay = isMultiDayEvent(b);

    if (aIsMultiDay && !bIsMultiDay) return -1;
    if (!aIsMultiDay && bIsMultiDay) return 1;

    return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
  });
}

/**
 * Get multi-day events that span across a specific day (but don't start on that day)
 */
export function getSpanningEventsForDay<T extends MeetingPublic>(events: T[], day: Date): T[] {
  return events.filter((event) => {
    if (!isMultiDayEvent(event)) return false;

    const eventStart = new Date(event.startDate);
    const eventEnd = new Date(event.endDate);

    // Only include if it's not the start day but is either the end day or a middle day
    return !isSameDay(day, eventStart) && (isSameDay(day, eventEnd) || (day > eventStart && day < eventEnd));
  });
}

/**
 * Get all events visible on a specific day (starting, ending, or spanning)
 */
export function getAllEventsForDay<T extends MeetingPublic>(events: T[], day: Date): T[] {
  return events.filter((event) => {
    const eventStart = new Date(event.startDate);
    const eventEnd = new Date(event.endDate);
    return isSameDay(day, eventStart) || isSameDay(day, eventEnd) || (day > eventStart && day < eventEnd);
  });
}

/**
 * Get all events for a day (for agenda view)
 */
export function getAgendaEventsForDay<T extends MeetingPublic>(events: T[], day: Date): T[] {
  return events
    .filter((event) => {
      const eventStart = new Date(event.startDate);
      const eventEnd = new Date(event.endDate);
      return isSameDay(day, eventStart) || isSameDay(day, eventEnd) || (day > eventStart && day < eventEnd);
    })
    .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
}

export const parseViewKey = (viewKey: string | null): VIEW_KEY => {
  if (!viewKey || !Object.values(VIEW_KEY).includes(viewKey as VIEW_KEY)) {
    return VIEW_KEY.DAY;
  } else {
    return viewKey as VIEW_KEY;
  }
};

export const parseCurrentDate = (dateString: string | null): Date => {
  if (!dateString) {
    return new Date();
  }
  const dateObject = parse(dateString, 'dd-MM-yyyy', new Date());
  if (isValid(dateObject)) {
    return dateObject;
  } else {
    return new Date();
  }
};
